<script setup="">
import { LockIcon } from "lucide-vue-next";
import { computed, inject } from "vue";

import InputError from "@/Components/Form/InputError.vue";
import SimpleLabel from "@/Components/Form/SimpleLabel.vue";
import SimpleDescription from "@/Components/SimpleDescription.vue";

const emit = defineEmits(["update:modelValue", "change"]);

const props = defineProps({
  label: {
    type: String,
    required: false
  },
  description: {
    type: String,
    required: false
  },
  error: {
    type: String,
    required: false
  },
  labelClass: {
    type: String,
    required: false
  },
  inputClass: {
    type: String,
    required: false
  },
  inputDefaultClass: {
    type: String,
    required: false,
    default: "form-select w-full border-2 h-12 pr-8"
  },
  tooltip: {
    type: String,
    required: false
  },
  items: {
    type: Array,
    required: false
  },
  options: {
    type: Array,
    required: false,
    default: () => []
  },
  modelValue: {
    type: [Object, String, Boolean, Number],
    required: false
  },
  value: {
    type: [Object, Boolean, String, Number],
    required: false
  },
  placeholder: {
    type: String,
    required: false
  },
  disabled: {
    type: Boolean,
    required: false
  },
  required: {
    type: Boolean,
    required: false,
    default: true
  }
});

const model = computed({
  get() {
    return props.modelValue ?? props.value;
  },
  set(value) {
    emit("update:modelValue", value);
    emit("change", value);
  }
});

const twMerge = inject("twMerge");

const computedIconClass = computed(() => twMerge("pointer-events-none pl-4 absolute inset-y-0 left-0 z-1 flex items-center", props.iconClass));

const computedClass = computed(() => twMerge(props.inputDefaultClass, props.inputClass));

const computedInputClass = computed(() =>
  twMerge(
    props.disabled
      ? "bg-50 dark:border-zinc-800 dark:bg-zinc-800/50"
      : "hover:border-500 focus:border-500 dark:border-200 dark:hover:border-zinc-500 dark:focus:border-zinc-500 dark:bg-zinc-900 dark:text-zinc-100",
    computedClass.value
  )
);
</script>

<template>
  <div>
    <SimpleLabel v-if="label && !$slots.label" :required="required" :classes="labelClass" :tooltip="tooltip" class="mb-2">
      {{ label }}
    </SimpleLabel>
    <slot v-if="$slots.label" name="label" />

    <div class="flex">
      <div class="relative flex flex-grow items-stretch focus-within:z-10">
        <template v-if="$slots.icon">
          <div ref="iconRef" :class="computedIconClass">
            <slot name="icon" />
          </div>
        </template>
        <!--end::prefix-->

        <select
          v-model="model"
          class="sizeable form-select block invalid:text-zinc-400"
          :class="[
            error ? 'border-error' : 'border-300',
            $slots.icon ? 'pl-12' : '',
            computedInputClass,
            {
              peer: $slots.icon || $slots.suffix
            }
          ]"
          :disabled="disabled"
          :required="required"
          v-bind="$attrs"
        >
          <slot>
            <option v-if="placeholder" value="" disabled selected>{{ placeholder }}</option>
            <template v-if="options && options.length > 0">
              <option v-for="option in options" :key="option.value" :value="option.value" :selected="option.default" :disabled="option.disabled">
                {{ option.label }}
              </option>
            </template>
            <template v-else>
              <slot name="options">
                <slot />
              </slot>
            </template>
          </slot>
        </select>

        <div v-if="disabled" class="absolute inset-y-0 right-3 z-1 flex items-center transition">
          <LockIcon class="h-3.5 w-3.5 text-400" />
        </div>

        <template v-if="$slots.suffix">
          <div class="absolute inset-y-0 right-0 z-1 flex items-center pr-2">
            <slot name="suffix" />
          </div>
        </template>
        <!--end::suffix-->
      </div>
    </div>

    <div v-if="error" class="relative mt-2">
      <transition name="fade">
        <InputError :message="error" />
      </transition>
    </div>

    <SimpleDescription v-if="description && !error">
      {{ description }}
    </SimpleDescription>
  </div>
</template>
